import React, { useState, useEffect } from 'react';
import { variablesAPI } from '../../services/variablesApi';
import './VariableInserter.css';

// Fallback categories if API fails
const FALLBACK_CATEGORIES = {
  contact: {
    label: 'Contact',
    variables: [
      { key: 'contact.firstName', label: 'First Name', example: 'John' },
      { key: 'contact.lastName', label: 'Last Name', example: 'Doe' },
      { key: 'contact.email', label: 'Email', example: 'john@example.com' },
      { key: 'contact.company', label: 'Company', example: 'Acme Inc' },
    ],
  },
  sender: {
    label: 'Sender',
    variables: [
      { key: 'sender.name', label: 'Sender Name', example: 'KNKT Music' },
      { key: 'sender.email', label: 'Sender Email', example: 'hello@knkt.io' },
      { key: 'sender.address', label: 'Address', example: '123 Music St' },
      { key: 'sender.city', label: 'City', example: 'Los Angeles' },
      { key: 'sender.state', label: 'State', example: 'CA' },
      { key: 'sender.zip', label: 'ZIP Code', example: '90001' },
    ],
  },
  release: {
    label: 'Release',
    variables: [
      { key: 'release.title', label: 'Release Title', example: 'My New Album' },
      { key: 'release.artist', label: 'Artist Name', example: 'Artist Name' },
      { key: 'release.label', label: 'Label', example: 'Record Label' },
      { key: 'release.releaseDate', label: 'Release Date', example: '2026-01-18' },
      { key: 'release.artwork.url', label: 'Artwork URL', example: 'https://...' },
      { key: 'release.genre', label: 'Genre', example: 'Electronic' },
    ],
  },
  links: {
    label: 'Links',
    variables: [
      { key: 'weblink', label: 'View in Browser', example: 'https://...' },
      { key: 'unsubscribeUrl', label: 'Unsubscribe Link', example: 'https://...' },
      { key: 'downloadUrl', label: 'Download Link', example: 'https://...' },
    ],
  },
  conditionals: {
    label: 'Conditionals & Loops',
    variables: [
      { key: '#if', label: 'If Statement', example: '{{#if variable}}...{{/if}}', isBlock: true },
      { key: '#unless', label: 'Unless Statement', example: '{{#unless variable}}...{{/unless}}', isBlock: true },
      { key: '#each', label: 'Loop (Each)', example: '{{#each items}}...{{/each}}', isBlock: true },
      { key: 'else', label: 'Else', example: '{{else}}', isBlock: true },
    ],
  },
};

const VariableInserter = ({ onInsert, targetField }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('contact');
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [loading, setLoading] = useState(false);

  // Load categories from API when component opens
  useEffect(() => {
    if (isOpen && Object.keys(categories).length === Object.keys(FALLBACK_CATEGORIES).length) {
      loadCategories();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await variablesAPI.getRegistry();
      if (response.success && response.data?.categories) {
        // Convert API format to component format
        const apiCategories = {};
        response.data.categories.forEach(cat => {
          apiCategories[cat.id] = {
            label: cat.label,
            variables: cat.variables,
          };
        });
        setCategories(apiCategories);
      }
    } catch (err) {
      console.error('Failed to load variables from API:', err);
      // Keep using fallback categories
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = (variable) => {
    let insertText;

    if (variable.isBlock) {
      // For block helpers, insert the full syntax
      insertText = variable.example;
    } else {
      // For regular variables, wrap in double curly braces
      insertText = `{{${variable.key}}}`;
    }

    onInsert(insertText);
    setIsOpen(false);
    setSearchQuery('');
  };

  const filteredVariables = () => {
    const category = categories[activeCategory];
    if (!category) return [];

    if (!searchQuery) {
      return category.variables;
    }

    return category.variables.filter(
      (v) =>
        v.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.key.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  return (
    <div className="variable-inserter">
      <button
        type="button"
        className="insert-variable-btn"
        onClick={() => setIsOpen(!isOpen)}
      >
        Insert Variable
      </button>

      {isOpen && (
        <div className="variable-inserter-dropdown">
          <div className="variable-inserter-header">
            <input
              type="text"
              placeholder="Search variables..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="variable-search"
            />
          </div>

          <div className="variable-inserter-body">
            <div className="variable-categories">
              {loading && <div className="loading-indicator">Loading...</div>}
              {Object.entries(categories).map(([key, category]) => (
                <button
                  key={key}
                  type="button"
                  className={`category-btn ${activeCategory === key ? 'active' : ''}`}
                  onClick={() => {
                    setActiveCategory(key);
                    setSearchQuery('');
                  }}
                >
                  {category.label}
                </button>
              ))}
            </div>

            <div className="variable-list">
              {filteredVariables().map((variable) => (
                <button
                  key={variable.key}
                  type="button"
                  className="variable-item"
                  onClick={() => handleInsert(variable)}
                >
                  <div className="variable-item-header">
                    <span className="variable-label">{variable.label}</span>
                    <code className="variable-code">
                      {variable.isBlock ? variable.example : `{{${variable.key}}}`}
                    </code>
                  </div>
                  <div className="variable-example">
                    Example: {variable.example}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariableInserter;
